import { useState } from 'react'
import { WaterfallBridge, CohortHeatmap, MixBars } from '../../charts'
import { CfoInteractiveChart } from '../../analysis'
import { CfoSegmentedControl } from '../../CfoPagePrimitives'
import { formatCurrencyExact, formatMonths, formatShare } from '../ceoFormat'
import type { CeoCockpitResponse } from '../ceoCockpitTypes'
import { chartWidthFor, useContainerWidth } from '../useContainerWidth'

const NOT_PREPARED = 'Not prepared'

/** Currency tile value; `null`/absent → "Not prepared" (never "—" or "$0"). */
function moneyTile(value: number | null | undefined): string {
  return value == null || !Number.isFinite(value) ? NOT_PREPARED : formatCurrencyExact(value)
}

export function CeoSaasPanel({ payload, onAsk }: { payload: CeoCockpitResponse; onAsk?: (prompt: string) => void }) {
  const saas = payload.saas
  const [mode, setMode] = useState<'logo' | 'revenue'>('logo')
  const [bridgeRef, bridgeHostWidth] = useContainerWidth<HTMLDivElement>()
  if (!saas) return null
  const bridgeWidth = chartWidthFor(bridgeHostWidth, 920, 560)
  const bridge = saas.bridge
  const cohorts = saas.cohorts
  const bySegment = Array.isArray(saas.by_segment) ? saas.by_segment : []
  const byChannel = Array.isArray(saas.by_channel) ? saas.by_channel : []
  const tiles: Array<[string, string]> = [
    ['MRR', moneyTile(saas.mrr)],
    ['ARR', moneyTile(saas.arr)],
    ['NRR', formatShare(saas.nrr)],
    ['GRR', formatShare(saas.grr)],
    ['Logo churn', formatShare(saas.logo_churn_pct)],
    ['ARPA', moneyTile(saas.arpa)],
    ['CAC', moneyTile(saas.cac)],
    ['LTV', moneyTile(saas.ltv)],
    ['LTV:CAC', saas.ltv_cac == null || !Number.isFinite(saas.ltv_cac) ? NOT_PREPARED : `${saas.ltv_cac.toFixed(1)}×`],
    ['Payback', formatMonths(saas.payback_months)],
  ]
  return (
    <section className="ceo-section rise" id="ceo-saas" data-testid="ceo-saas">
      <p className="ceo-kicker">SaaS</p>
      <div className="ceo-saas-tiles">
        {tiles.map(([label, value]) => (
          <article key={label} className={`ceo-mini-tile${value === NOT_PREPARED ? ' is-empty' : ''}`}>
            <span className="ceo-kicker">{label}</span>
            <b>{value}</b>
          </article>
        ))}
      </div>
      {bridge ? (
        <CfoInteractiveChart hint="Ask AI to explain the MRR bridge" onAnalyze={() => onAsk?.('Explain the MRR bridge this morning.')} ariaLabel="Explain MRR bridge">
          <div className="ceo-bridge" ref={bridgeRef}>
            <WaterfallBridge
              start={{ label: 'Start', value: bridge.start }}
              steps={[
                { label: 'New', delta: bridge.new },
                { label: 'Expansion', delta: bridge.expansion },
                { label: 'Contraction', delta: bridge.contraction },
                { label: 'Churn', delta: bridge.churn },
                { label: 'Reactivation', delta: bridge.reactivation || 0 },
              ]}
              end={{ label: 'End', value: bridge.end }}
              width={bridgeWidth}
              ariaLabel="MRR bridge"
              formatValue={(value) => formatCurrencyExact(value)}
            />
          </div>
        </CfoInteractiveChart>
      ) : (
        <div className="ceo-not-tracked is-compact">
          <h3>MRR bridge is not prepared</h3>
          <p>The bridge fills once two consecutive months of recurring invoices are on the books.</p>
        </div>
      )}
      {cohorts && Array.isArray(cohorts.rows) && cohorts.rows.length ? (
        <div className="ceo-saas-cohorts">
          <CfoSegmentedControl
            label="Cohort mode"
            value={mode}
            onChange={(value) => setMode(value as 'logo' | 'revenue')}
            options={[{ id: 'logo', label: 'Logos' }, { id: 'revenue', label: 'Revenue' }]}
          />
          <CohortHeatmap ages={cohorts.ages || []} rows={cohorts.rows} mode={mode} ariaLabel="SaaS cohorts" />
        </div>
      ) : (
        <p className="ceo-muted">Cohorts are not prepared yet — they need at least one cohort month with a full month of age.</p>
      )}
      {bySegment.length || byChannel.length ? (
        <MixBars
          ariaLabel="SaaS mix"
          stacks={[
            ...(bySegment.length ? [{ key: 'segment', label: 'Segment', slices: bySegment.slice(0, 4).map((row) => ({ key: row.key, label: row.label, share: row.share_ytd })) }] : []),
            ...(byChannel.length ? [{ key: 'channel', label: 'Channel', slices: byChannel.slice(0, 4).map((row) => ({ key: row.key, label: row.label, share: row.share_ytd })) }] : []),
          ]}
        />
      ) : null}
    </section>
  )
}
